import React, { useState, useCallback, useEffect } from 'react';
import { Tags, Plus, X, AlertCircle, CheckCircle, Info } from 'lucide-react';
import clsx from 'clsx';
import { CategoryMode, CategoryInputProps, validateCategoryName, formatCategoryName } from '../types';
import { useApp } from '../contexts/AppContext';

/**
 * CategoryInput Component - Custom category management with validation
 * 
 * Features:
 * - Mode selection: predefined, custom, or hybrid categories
 * - Dynamic category addition and removal
 * - Real-time category name validation
 * - Visual feedback for validation states
 * - localStorage persistence for user preferences
 * - Integration with job submission workflow
 */
export const CategoryInput: React.FC<CategoryInputProps> = ({
  onCategoriesChange,
  initialMode: _initialMode = 'predefined',
  initialCategories = [],
  required = false
}) => {
  const { state, stopCategoryHighlight } = useApp();
  const { shouldHighlightCategories } = state;
  
  // Load initial state from localStorage or props
  const [categoryMode, setCategoryMode] = useState<CategoryMode>(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('sortingcat_category_mode') : null;
      return (saved as CategoryMode) || 'predefined'; // Force default to predefined
    } catch (error) {
      return 'predefined'; // Fallback if localStorage is not available
    }
  });

  const [customCategories, setCustomCategories] = useState<string[]>(() => {
    if (initialCategories.length > 0) return initialCategories;
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem('sortingcat_custom_categories') : null;
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      return []; // Fallback if localStorage is not available or JSON parsing fails
    }
  });

  const [currentInput, setCurrentInput] = useState('');
  const [isTouched, setIsTouched] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  // Validate current categories and notify parent
  const validateAndNotify = useCallback(() => {
    let isValid = true;
    let categories: string[] = [];

    if (categoryMode === 'predefined') {
      isValid = true; // Always valid for predefined mode
    } else if (categoryMode === 'custom') {
      categories = customCategories;
      isValid = customCategories.length >= (required ? 1 : 0);
    } else if (categoryMode === 'hybrid') {
      categories = customCategories;
      isValid = true; // Hybrid mode is always valid (falls back to predefined)
    }

    onCategoriesChange(categories, categoryMode, isValid);
  }, [categoryMode, customCategories, required, onCategoriesChange]);

  // Validate and notify on state changes
  useEffect(() => {
    validateAndNotify();
  }, [validateAndNotify]);

  // Handle mode change
  const handleModeChange = useCallback((mode: CategoryMode) => {
    setCategoryMode(mode);
    setIsTouched(true);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('sortingcat_category_mode', mode);
      }
    } catch (error) {
      console.warn('Failed to save category mode to localStorage:', error);
    }
    
    // Stop category highlight when user interacts with categories
    if (shouldHighlightCategories) {
      stopCategoryHighlight();
    }
  }, [shouldHighlightCategories, stopCategoryHighlight]);

  // Handle category input changes with comma-separated support
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setCurrentInput(value);

    if (!isTouched) {
      setIsTouched(true);
    }

    // Clear previous error
    setInputError(null);

    // Check if input contains comma (for comma-separated validation)
    if (value.includes(',')) {
      const categories = value.split(',').map(c => c.trim()).filter(c => c.length > 0);
      const totalAfterAdd = customCategories.length + categories.length;

      if (totalAfterAdd > 20) {
        setInputError(`Would exceed maximum of 20 categories (currently have ${customCategories.length}, trying to add ${categories.length})`);
        return;
      }

      // Validate each category
      const invalidCategories = categories.filter(cat => !validateCategoryName(cat));
      if (invalidCategories.length > 0) {
        setInputError(`Invalid category: "${invalidCategories[0]}" - use only letters, numbers, spaces, hyphens, and underscores`);
        return; // eslint-disable-line no-useless-return
      }
    } else {
      // Validate single input as user types
      if (value.length > 0 && !validateCategoryName(value)) {
        if (value.length < 2) {
          setInputError('Category name must be at least 2 characters');
        } else if (value.length > 50) {
          setInputError('Category name must be 50 characters or less');
        } else {
          setInputError('Use only letters, numbers, spaces, hyphens, and underscores');
        }
      }
    }
  }, [isTouched, customCategories.length]);

  // Add category with comma-separated support
  const handleAddCategory = useCallback(() => {
    const trimmed = currentInput.trim();

    // Check if input contains commas (comma-separated list)
    if (trimmed.includes(',')) {
      const categories = trimmed.split(',')
        .map(c => c.trim())
        .filter(c => c.length > 0);

      // Validate all categories
      const invalidCategories = categories.filter(cat => !validateCategoryName(cat));
      if (invalidCategories.length > 0) {
        setInputError(`Invalid category: "${invalidCategories[0]}"`);
        return;
      }

      // Check max limit
      if (customCategories.length + categories.length > 20) {
        setInputError(`Cannot add ${categories.length} categories. Maximum 20 total (currently have ${customCategories.length})`);
        return;
      }

      // Format and add all categories
      const formattedCategories = categories.map(cat => formatCategoryName(cat));
      const newCategories = [...customCategories, ...formattedCategories];
      setCustomCategories(newCategories);
      setCurrentInput('');
      setInputError(null);

      // Persist to localStorage
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('sortingcat_custom_categories', JSON.stringify(newCategories));
        }
      } catch (error) {
        console.warn('Failed to save custom categories to localStorage:', error);
      }
      return;
    }

    // Single category validation
    if (!validateCategoryName(trimmed)) {
      setInputError('Invalid category name');
      return;
    }

    const formatted = formatCategoryName(trimmed);

    if (customCategories.length >= 20) {
      setInputError('Maximum 20 categories allowed');
      return;
    }

    const newCategories = [...customCategories, formatted];
    setCustomCategories(newCategories);
    setCurrentInput('');
    setInputError(null);

    // Persist to localStorage
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('sortingcat_custom_categories', JSON.stringify(newCategories));
      }
    } catch (error) {
      console.warn('Failed to save custom categories to localStorage:', error);
    }
  }, [currentInput, customCategories]);

  // Remove category
  const handleRemoveCategory = useCallback((categoryToRemove: string) => {
    const newCategories = customCategories.filter(cat => cat !== categoryToRemove);
    setCustomCategories(newCategories);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('sortingcat_custom_categories', JSON.stringify(newCategories));
      }
    } catch (error) {
      console.warn('Failed to save custom categories to localStorage:', error);
    }
  }, [customCategories]);

  // Handle Enter key
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && currentInput.trim()) {
      e.preventDefault();
      handleAddCategory();
    }
  }, [currentInput, handleAddCategory]);

  // Visual states
  const showInputError = inputError && currentInput.length > 0;
  const canAddCategory = currentInput.trim().length > 0 && !inputError;
  const isCustomMode = categoryMode === 'custom' || categoryMode === 'hybrid';

  // Auto-stop highlight animation after 10 seconds
  useEffect(() => {
    if (shouldHighlightCategories) {
      const timeout = setTimeout(() => {
        stopCategoryHighlight();
      }, 10000); // Stop highlighting after 10 seconds
      
      return () => clearTimeout(timeout);
    }
  }, [shouldHighlightCategories, stopCategoryHighlight]);

  return (
    <div className="w-full space-y-4">
      <div className="p-4 border-b border-gray-200 text-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
          <Tags className="w-5 h-5 mr-2" />
          3) Select Category Mode (optional)
        </h2>
      </div>
      
      {/* Mode Selection */}
      <div>
        <label 
          className={clsx(
            "block text-sm font-medium text-gray-700 mb-3 transition-all duration-300",
            shouldHighlightCategories && "animate-pulse text-purple-600"
          )}
        >
          {shouldHighlightCategories ? (
            <span className="font-semibold">🎨 Choose how to categorize your images →</span>
          ) : (
            <>Category Mode {required && <span className="text-red-500">*</span>}</>
          )}
        </label>
        
        <div className={clsx(
          "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 transition-all duration-300",
          shouldHighlightCategories && "ring-2 ring-purple-300 ring-opacity-50 rounded-lg p-2"
        )}>
          <button
            type="button"
            onClick={() => handleModeChange('predefined')}
            className={clsx(
              'p-3 border rounded-lg text-left transition-colors focus:ring-2 focus:outline-none',
              categoryMode === 'predefined'
                ? 'border-blue-500 bg-blue-50 text-blue-900 ring-blue-200'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-200'
            )}
          >
            <div className="font-medium text-sm">Standard AI Categories</div>
            <div className="text-xs text-gray-600 mt-1">
              Let AI choose the best categories
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleModeChange('custom')}
            className={clsx(
              'p-3 border rounded-lg text-left transition-colors focus:ring-2 focus:outline-none',
              categoryMode === 'custom'
                ? 'border-green-500 bg-green-50 text-green-900 ring-green-200'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:border-green-500 focus:ring-green-200'
            )}
          >
            <div className="font-medium text-sm">Custom Categories Only</div>
            <div className="text-xs text-gray-600 mt-1">
              Use only your specified categories
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleModeChange('hybrid')}
            className={clsx(
              'p-3 border rounded-lg text-left transition-colors focus:ring-2 focus:outline-none',
              categoryMode === 'hybrid'
                ? 'border-purple-500 bg-purple-50 text-purple-900 ring-purple-200'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:border-purple-500 focus:ring-purple-200'
            )}
          >
            <div className="font-medium text-sm">Hybrid Mode</div>
            <div className="text-xs text-gray-600 mt-1">
              Custom + AI fallback categories
            </div>
          </button>
        </div>
      </div>

      {/* Custom Category Input */}
      {isCustomMode && (
        <div>
          <label htmlFor="category-input" className="block text-sm font-medium text-gray-700 mb-2">
            Add Category
          </label>
          
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                id="category-input"
                type="text"
                value={currentInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter categories (comma-separated: red, blue, documents)"
                className={clsx(
                  'input-enhanced placeholder-gray-400',
                  showInputError && 'border-red-300 focus:border-red-500 focus:ring-red-200',
                  !showInputError && 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                )}
                aria-describedby={showInputError ? 'category-input-error' : undefined}
                aria-invalid={showInputError ? 'true' : 'false'}
              />
              
              {/* Input validation icon */}
              {currentInput && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {showInputError ? (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  ) : canAddCategory ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : null}
                </div>
              )}
            </div>
            
            <button
              type="button"
              onClick={handleAddCategory}
              disabled={!canAddCategory}
              className={clsx(
                'btn-primary btn-sm flex items-center justify-center min-w-[48px]',
                !canAddCategory && 'opacity-50 cursor-not-allowed'
              )}
              aria-label="Add category"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Input Error */}
          {showInputError && (
            <p id="category-input-error" className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {inputError}
            </p>
          )}
        </div>
      )}

      {/* Category List */}
      {isCustomMode && customCategories.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Custom Categories ({customCategories.length}/20)
            </span>
            {customCategories.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setCustomCategories([]);
                  try {
                    if (typeof window !== 'undefined') {
                      localStorage.removeItem('sortingcat_custom_categories');
                    }
                  } catch (error) {
                    console.warn('Failed to remove custom categories from localStorage:', error);
                  }
                }}
                className="text-xs text-red-600 hover:text-red-800 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            {customCategories.map((category) => (
              <span
                key={category}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
              >
                <Tags className="w-3 h-3" />
                {category}
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(category)}
                  className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                  aria-label={`Remove ${category} category`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="text-xs text-gray-500 space-y-1">
        <p className="flex items-start">
          <Info className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
          <span>
            {categoryMode === 'predefined' && 'AI will automatically categorize your images using intelligent analysis.'}
            {categoryMode === 'custom' && 'Images will only be sorted into the categories you specify above.'}
            {categoryMode === 'hybrid' && 'AI will try to use your categories first, then fall back to standard categories.'}
          </span>
        </p>
        {isCustomMode && (
          <p className="text-gray-400">
            • Separate multiple categories with commas (e.g., red, blue, green)
            • Category names will be formatted automatically (lowercase with dashes)
            • Use descriptive names like &ldquo;red-items&rdquo;, &ldquo;documents&rdquo;, &ldquo;family-photos&rdquo;
            • Maximum 20 categories per job
          </p>
        )}
      </div>
    </div>
  );
};

export default CategoryInput;